// =============================================
// Content Engine Orchestrator
// v3.0: Full pipeline SERP → KG → Fan-Out → Outline → Draft → Enrichment → Optimization → Humanization
// =============================================

import { supabase } from "@/integrations/supabase/client";
import { buildNodeContentContext, createContextSnapshot, buildContextHash } from "./nodeContentContext";
import { researchSERP } from "./serpResearch";
import { assembleKnowledgeGraph } from "./knowledgeGraph";
import { generateFanOut } from "./queryFanOut";
import { consolidateAreas } from "./areaConsolidation";
import { ensureActiveWritingProfile } from "./writingProfileGenerator";
import { generateOutline } from "./outlineGenerator";
import { generateDraft } from "./draftGenerator";
import { weaveLinks } from "./linkWeaver";
import { isStandaloneMode } from "@/services/llm/promptLoader";
import { extractClaims, getVerifiableClaims, buildThesisFallbackClaim } from "./claimExtractor";
import { mergeFreshEav } from "./eavGuard";
import { verifyClaims, insertSources, stripFabricatedSources } from "./webSearchVerifier";
import { optimizeArticle } from "./optimizationService";
import { humanizeArticle } from "./humanizationService";
// inContentImagesService importowane bezposrednio w publishQueueService (dynamic import)
import { postContentAudit } from "./postContentAudit";
import { generateMicroEav } from "./microEavGenerator";
import { shouldSkipNodeGeneration, updateNodeSkipStatus } from "./skipCheck";
// Pillar legacy import removed in CLEANUP v1 #1 (2026-06-01).
// Pillar generation żyje w pillarGenerator.ts (osobny entry point, faza P-P2).
// ContentEnginePanel.tsx gate blokuje wywołanie generateArticle/Structure/Content
// dla document_role='pillar' (commit 3175dd5).
import type { EngineStage } from "@/types/thematicMaps";
import { logStage, logDb, logProfile, logError, logDebug, startLogRun, endLogRun, logPublic } from "./contentLogger";

// =============================================
// Types
// =============================================

export interface CostBreakdown {
  serp_research: number;
  kg_assembly: number;
  /** Micro-EAV (stage 2.5) — dopisany do interface/sum 2026-08-23 (wczesniej ustawiany, ale NIE liczony do total). */
  micro_eav: number;
  fan_out: number;
  outline: number;
  draft: number;
  /** Link Weaver (etap 5.5) — 1 call gpt-4.1-mini po sklejeniu draftu. */
  link_weaving: number;
  enrichment: number;
  optimization: number;
  humanization: number;
  in_content_images: number;
  total: number;
}

export interface ArticleResult {
  success: boolean;
  stage: EngineStage;
  draftHtml?: string;
  error?: string;
  cost: CostBreakdown;
  durationMs: number;
}

export interface EngineProgress {
  stage: EngineStage;
  stageLabel: string;
  blockProgress?: { current: number; total: number };
  stageDebug?: Record<string, unknown>;
}

const STAGE_LABELS: Record<string, string> = {
  serp_research: "Badanie SERP...",
  kg_assembly: "Budowanie grafu wiedzy...",
  fan_out: "Ekspansja mikro-intencji...",
  outline: "Generowanie outline...",
  draft: "Pisanie artykułu...",
  enrichment: "Wzbogacanie danymi...",
  optimization: "Optymalizacja artykułu...",
  humanization: "Humanizacja treści...",
};

// =============================================
// Helper: update engine stage in DB
// =============================================

async function updateEngineStage(
  nodeId: string,
  stage: EngineStage,
  extra?: Record<string, unknown>,
): Promise<void> {
  const payload = {
    engine_stage: stage,
    ...extra,
  };
  const { error } = await (supabase as any)
    .from("thematic_node_content")
    .update(payload)
    .eq("node_id", nodeId);

  await logDb("PATCH", "thematic_node_content", nodeId, payload, { error });

  if (error) {
    logError("DB", `PATCH FAILED for stage=${stage}, attempting read-back...`);
    const { data: readBack } = await (supabase as any)
      .from("thematic_node_content")
      .select("engine_stage, draft_html, final_html, qa_status")
      .eq("node_id", nodeId)
      .maybeSingle();
    if (readBack) {
      logDebug("DB", `READ-BACK: engine_stage=${readBack.engine_stage}, draft_html=${readBack.draft_html ? readBack.draft_html.length + " chars" : "null"}, final_html=${readBack.final_html ? readBack.final_html.length + " chars" : "null"}, qa=${readBack.qa_status}`);
    }
  }
}

// =============================================
// Main orchestrator
// =============================================

/**
 * Generate a complete article for a node.
 * Runs through all 5 stages of the Content Engine.
 */
export async function generateArticle(
  nodeId: string,
  mapId: string,
  onProgress?: (progress: EngineProgress) => void,
): Promise<ArticleResult> {
  // Existing Posts Awareness: skip nodow ktore maja matched wpis na WP (keep/partial/pending-approval)
  const skipDec = await shouldSkipNodeGeneration(nodeId);
  if (skipDec.skip && skipDec.reason) {
    await updateNodeSkipStatus(nodeId, skipDec.reason);
    return {
      success: true,
      stage: "skipped_existing" as EngineStage,
      cost: {
        serp_research: 0, kg_assembly: 0, micro_eav: 0, fan_out: 0, outline: 0,
        draft: 0, link_weaving: 0, enrichment: 0, optimization: 0, humanization: 0, in_content_images: 0, total: 0,
      },
      durationMs: 0,
    };
  }

  const startTime = Date.now();
  const engineDebug: Record<string, unknown> = {};
  const cost: CostBreakdown = {
    serp_research: 0,
    kg_assembly: 0,
    micro_eav: 0,
    fan_out: 0,
    outline: 0,
    draft: 0,
    link_weaving: 0,
    enrichment: 0,
    optimization: 0,
    humanization: 0,
    in_content_images: 0,
    total: 0,
  };

  // Start log collector run (only active in debug mode)
  startLogRun(`article-${nodeId}`, { nodeId, mapId, type: "full_pipeline" });

  try {
    // 0. Build NodeContentContext — one-time fetch of ALL data needed
    onProgress?.({ stage: "serp_research", stageLabel: "Budowanie kontekstu node'a..." });

    const ctx = await buildNodeContentContext(nodeId, mapId);
    const contextHash = buildContextHash(ctx);
    engineDebug.input_context = { ...createContextSnapshot(ctx), context_hash: contextHash };

    const query = ctx.node.canonical_question;
    const seoTitle = ctx.node.title;
    const language = ctx.map.language;

    // Ensure thematic_node_content exists
    await (supabase as any).from("thematic_node_content").upsert(
      {
        node_id: nodeId,
        map_id: mapId,
        user_id: (await supabase.auth.getSession()).data.session?.user?.id,
        engine_stage: "serp_research",
        engine_started_at: new Date().toISOString(),
        content_status: "empty",
      },
      { onConflict: "node_id" },
    );

    // ---- STAGE 1: SERP Research ----
    onProgress?.({ stage: "serp_research", stageLabel: `SERP Research — query: "${query}"` });
    await updateEngineStage(nodeId, "serp_research");

    const serpResult = await researchSERP(nodeId, query, mapId);
    cost.serp_research = 0.015;

    engineDebug.serp = serpResult.debug;

    // Build detailed SERP log with per-URL errors visible
    if (serpResult.cacheHit) {
      onProgress?.({ stage: "serp_research", stageLabel: `  📦 Cache hit — ${serpResult.debug.blocks_passed} bloków z cache (30 dni TTL)` });
    }
    // Log per-URL results for transparency (works for both cache and fresh)
    for (const urlInfo of (serpResult.debug as any).per_url || []) {
      const domain = urlInfo.url.replace(/https?:\/\//, "").split("/")[0];
      if (urlInfo.crawled) {
        onProgress?.({ stage: "serp_research", stageLabel: `    ✓ ${domain} — ${urlInfo.chars_total} znaków → ${urlInfo.chars_kept} kept` });
      } else {
        onProgress?.({ stage: "serp_research", stageLabel: `    ✗ ${domain} — ${urlInfo.error || "unknown error"}` });
      }
    }
    const totalCharsGA = (serpResult.debug as any).total_chars_after ?? serpResult.cleanContent.reduce((s: number, b: any) => s + (b.text?.length || 0), 0);
    onProgress?.({
      stage: "serp_research",
      stageLabel: `SERP ✓ ${serpResult.debug.urls_crawled}/${serpResult.debug.urls_fetched} URLs, ${serpResult.debug.blocks_passed} bloków [${serpResult.debug.filter_method}], ${totalCharsGA} znaków`,
      stageDebug: serpResult.debug,
    });

    // ---- STAGE 2: Knowledge Graph Assembly ----
    onProgress?.({ stage: "kg_assembly", stageLabel: `KG Assembly — query: "${query}", entity: "${ctx.node.primary_entity}", ${serpResult.cleanContent.length} źródeł → LLM...` });
    await updateEngineStage(nodeId, "kg_assembly");

    const { kg, costUsd: kgCost, debug: kgDebug } = await assembleKnowledgeGraph(
      nodeId,
      mapId,
      serpResult.cleanContent,
      query,
      ctx.node.primary_entity,
      language,
      ctx.knowledge,
      (subLabel) => onProgress?.({ stage: "kg_assembly", stageLabel: `  ${subLabel}` }),
      { document_role: ctx.node.document_role, boundaries_not_owned: ctx.map.boundaries_not_owned },
    );
    cost.kg_assembly = kgCost;

    engineDebug.kg = kgDebug;
    const kgWarnings: string[] = [];
    if (kgDebug.facts === 0) kgWarnings.push("⚠  0 faktów!");
    if (kgDebug.entities < 5) kgWarnings.push(`⚠  mało encji (${kgDebug.entities})`);

    onProgress?.({
      stage: "kg_assembly",
      stageLabel: `KG ✓ ${kgDebug.entities} encji, ${kgDebug.facts} faktów, ${kgDebug.relationships} relacji, ${kgDebug.map_entities_merged} merged — $${kgCost.toFixed(3)}${kgWarnings.length > 0 ? " " + kgWarnings.join(" ") : ""}`,
      stageDebug: kgDebug,
    });

    // ---- STAGE 2.5: Micro-EAV for KG entities without EAV ----
    // Propagacja entity_type: KG zapisuje `type: mapEntity.entity_type || "Concept"` —
    // Micro-EAV uzywa tego do prompt'u + INSERT do thematic_entities zeby Brand/Organization
    // dostaly inne atrybuty niz Concept.
    const microEavResult = await generateMicroEav(
      nodeId, mapId,
      kg.entities.map((e: any) => ({
        entity_name: e.entity_name,
        description: e.description,
        entity_type: e.type,
      })),
      language,
      { map_name: ctx.map.name, macro_statement: ctx.map.macro_statement, scope_in: ctx.map.scope_in || [], scope_out: ctx.map.scope_out || [] },
      seoTitle, ctx.node.document_role, ctx.node.canonical_question,
    );
    if (microEavResult.generatedCount > 0) {
      // GUARD 2026-08-24 (root cause awarii 21-24.08): stary re-fetch robil
      // .in() na WSZYSTKICH encjach mapy (Whiteever: 968 id ~37KB w URL) ->
      // serwer odrzucal request, blad byl polykany -> ctx.knowledge.eav=[] ->
      // fan-out/outline/draft bez EAV -> tekst bez liczb -> 0 twierdzen ->
      // BRAK ZRODEL. Teraz: fetch TYLKO dla encji z tego przebiegu Micro-EAV
      // (male .in()), a MERGE przez mergeFreshEav — blad/pusta lista NIGDY
      // nie nadpisuje niepustego stanu (zasada, patrz eavGuard.ts).
      // entityNameToDbId zawiera WSZYSTKIE encje mapy (Whiteever: 968) —
      // zawezamy do encji KG TEGO runu (~20-30), inaczej .in() znow pęka
      // (guard to zlapal jako "Bad Request" w pierwszym runie po fixie)
      const kgEntityNames = new Set(
        kg.entities.map((e: any) => String(e.entity_name || "").toLowerCase()).filter(Boolean),
      );
      const microEntries = Object.entries(microEavResult.entityNameToDbId || {}).filter(
        ([name]) => kgEntityNames.has(name.toLowerCase()),
      );
      const microIds = microEntries.map(([, id]) => id);
      const microIdToName = new Map(microEntries.map(([name, id]) => [id, name]));
      let freshRows: any[] | null = null;
      let freshError: unknown = null;
      if (microIds.length > 0) {
        const { data, error } = await (supabase as any)
          .from("thematic_entity_attributes")
          .select("entity_id, attribute_name, value, confidence")
          .in("entity_id", microIds)
          .gte("confidence", 0.7)
          .limit(50);
        freshRows = data;
        freshError = error;
      }
      const refresh = mergeFreshEav(
        ctx.knowledge.eav,
        (freshRows || []).map((a: any) => ({
          entity_name: microIdToName.get(a.entity_id) || a.entity_id,
          attribute_name: a.attribute_name,
          value: a.value,
          confidence: typeof a.confidence === "number" ? a.confidence : 0.7,
        })),
        { error: freshError, source: "Micro-EAV refresh" },
      );
      if (refresh.warning) logPublic("EAV", refresh.warning);
      else if (refresh.merged > 0) logDebug("EAV", `Micro-EAV merge: +${refresh.merged} atrybutów (razem ${refresh.eav.length})`);
      ctx.knowledge.eav = refresh.eav;
      onProgress?.({ stage: "kg_assembly", stageLabel: `Micro-EAV ✓ ${microEavResult.generatedCount} atrybutów — $${microEavResult.costUsd.toFixed(4)}` });
    }
    cost.micro_eav = microEavResult.costUsd;
    engineDebug.micro_eav = microEavResult.debug;

    // ---- STAGE 3: Query Fan-Out ----
    onProgress?.({ stage: "fan_out", stageLabel: `Fan-Out — rozkład query na mikro-intencje...` });
    await updateEngineStage(nodeId, "fan_out");

    const { fanOut, costUsd: fanOutCost, debug: fanOutDebug } = await generateFanOut(
      nodeId,
      query,
      mapId,
      ctx.node.document_role,
      ctx.node.primary_entity,
      language,
      ctx.knowledge.eav,
      {
        seed_intent_family: ctx.seed.intent_family,
        macro_statement: ctx.map.macro_statement,
        boundaries_not_owned: ctx.map.boundaries_not_owned,
        market_keywords_top5: ctx.serp?.market_keywords_top5,
      },
      kg.ideations,
    );
    cost.fan_out = fanOutCost;

    engineDebug.fan_out = fanOutDebug;
    onProgress?.({
      stage: "fan_out",
      stageLabel: `Fan-Out ✓ ${fanOutDebug.micro_areas} mikro-obszarów, intent: ${fanOutDebug.primary_intent}, PAA: ${fanOutDebug.paa_merged} — $${fanOutCost.toFixed(3)}`,
      stageDebug: fanOutDebug,
    });

    // ---- STAGE 3b: Area Consolidation (optional) ----
    const consolidationResult = await consolidateAreas(fanOut, query, ctx.node.document_role, ctx.node.primary_entity, mapId, nodeId);
    if (consolidationResult.consolidated) {
      cost.fan_out += consolidationResult.costUsd;
      onProgress?.({
        stage: "fan_out",
        stageLabel: `Consolidation ✓ ${consolidationResult.originalCount} → ${consolidationResult.newCount} areas (${consolidationResult.mergesMade} merges)`,
      });
    }
    engineDebug.area_consolidation = consolidationResult;

    // ---- STAGE 4: Outline + Distribution ----
    onProgress?.({ stage: "outline", stageLabel: `Outline — budowanie struktury H2/H3...` });
    await updateEngineStage(nodeId, "outline");

    // CLEANUP v1 #1 (2026-06-01): legacy isPillar branch removed.
    // Pillar żyje w pillarGenerator.ts (osobny entry point z faza P-P2). ContentEnginePanel
    // gate (commit 3175dd5) blokuje wywołanie generateArticle dla document_role='pillar' z UI.
    // Spoki ZAWSZE szły gałęzią `: undefined` (isPillar=false), więc to no-op dla spoke flow.

    const { outline, costUsd: outlineCost, debug: outlineDebug } = await generateOutline(
      nodeId,
      mapId,
      fanOut,
      kg,
      seoTitle,
      language,
      {
        document_role: ctx.node.document_role,
        primary_entity: ctx.node.primary_entity,
        canonical_question: ctx.node.canonical_question,
        validated_question: ctx.serp?.validated_question,
        best_market_keyword: ctx.serp?.best_market_keyword,
        market_keywords_top5: ctx.serp?.market_keywords_top5,
        boundaries_not_owned: ctx.map.boundaries_not_owned,
      },
      ctx.knowledge.eav,
      microEavResult.entityNameToDbId,
      undefined, // outlineOptions: pillar legacy removed (CLEANUP v1 #1)
    );
    cost.outline = outlineCost;

    engineDebug.outline = outlineDebug;
    onProgress?.({
      stage: "outline",
      stageLabel: `Outline ✓ ${outlineDebug.h2_sections} H2, ${outlineDebug.h3_total} H3, ${outlineDebug.kg_items_distributed} KG items — $${outlineCost.toFixed(3)}`,
      stageDebug: {
        ...outlineDebug,
        sections: outline.sections.map((s) => ({
          type: s.type, order: s.order, header: s.header, section_type: s.section_type,
          h3s: (s.h3s || []).map((h) => h.header),
          source_area: s.source_area, source_intent: s.source_intent,
        })),
      },
    });

    // ---- STAGE 5: Block-by-Block Draft ----
    const totalBlocks = outline.sections.filter((s) => s.type === "h2").length;
    onProgress?.({ stage: "draft", stageLabel: `Draft — ${totalBlocks} bloków do napisania...` });
    await updateEngineStage(nodeId, "draft");

    const { draftHtml, costUsd: draftCost, debug: draftDebug } = await generateDraft(
      nodeId,
      mapId,
      outline,
      kg,
      seoTitle,
      language,
      (current, total) => {
        onProgress?.({
          stage: "draft",
          stageLabel: `Draft — blok ${current}/${total}...`,
          blockProgress: { current, total },
        });
      },
      ctx.writingProfile,
      ctx.knowledge.internal_link_suggestions,
      ctx.node.document_role,
      ctx.knowledge.eav,
      {
        primary_entity: ctx.node.primary_entity,
        canonical_question: ctx.node.canonical_question,
      },
      undefined, // draftOptions: pillar legacy removed (CLEANUP v1 #1)
    );
    cost.draft = draftCost;

    engineDebug.draft = draftDebug;
    onProgress?.({
      stage: "draft",
      stageLabel: `Draft ✓ ${draftDebug.total_words} słów, ${draftDebug.blocks_generated} bloków — $${draftCost.toFixed(3)}`,
      stageDebug: draftDebug,
    });


    // ---- STAGE 5.5: Link Weaving ("najpierw pisz, potem linkuj") ----
    // Jeden call na caly artykul; kod aplikuje plan deterministycznie (max 1x
    // per target). Standalone pomijany — artykuly bez linkow wewnetrznych.
    let weavedHtml = draftHtml;
    if (!isStandaloneMode()) {
      onProgress?.({ stage: "draft", stageLabel: "Link Weaver — wplatanie linkow wewnetrznych..." });
      const weave = await weaveLinks(draftHtml, ctx.knowledge.internal_link_suggestions.slice(0, 5), {
        language,
        selfSlug: ctx.node.slug,
        nodeId,
      });
      weavedHtml = weave.html;
      cost.link_weaving = weave.costUsd;
      engineDebug.link_weaving = weave.debug;
      const appliedN = Array.isArray((weave.debug as Record<string, unknown>).applied)
        ? ((weave.debug as Record<string, unknown>).applied as unknown[]).length
        : 0;
      onProgress?.({ stage: "draft", stageLabel: `Link Weaver ✓ ${appliedN} linkow wplecionych — $${weave.costUsd.toFixed(4)}` });
    }

    // ---- STAGE 6: Data Enrichment ----
    const wordCount = weavedHtml.split(/\s+/).length;
    onProgress?.({ stage: "enrichment", stageLabel: `Enrichment — ekstrakcja claimów z ${wordCount} słów...` });
    await updateEngineStage(nodeId, "enrichment");

    let claims = await extractClaims(weavedHtml, language);
    if (claims.length === 0) {
      // FALLBACK (2026-08-24): slowniki/definicje bez liczb daja 0 twierdzen
      // -> weryfikacja nie startowala -> artykul bez zrodel. Weryfikujemy
      // pierwsze zdanie wstepu (glowna teze) przez web_search.
      const thesis = buildThesisFallbackClaim(weavedHtml, ctx.node.title);
      if (thesis) {
        claims = [thesis];
        logPublic("ENRICHMENT", "0 twierdzeń z ekstraktora — fallback: weryfikacja głównej tezy artykułu przez web_search");
      }
    }
    const verifiableClaims = getVerifiableClaims(claims);
    let enrichedHtml = weavedHtml;

    onProgress?.({
      stage: "enrichment",
      stageLabel: `Enrichment — ${claims.length} claimów, ${verifiableClaims.length} do weryfikacji...`,
    });

    const enrichDebug: Record<string, unknown> = {
      claims_found: claims.length,
      claims_verified: verifiableClaims.length,
      language_used: language,
      claims_by_trigger: {
        date: claims.filter((c) => c.triggers.includes("DATE")).length,
        numeric_fact: claims.filter((c) => c.triggers.includes("NUMERIC_FACT")).length,
        named_reference: claims.filter((c) => c.triggers.includes("NAMED_REFERENCE")).length,
        source_stem: claims.filter((c) => c.triggers.includes("SOURCE_STEM")).length,
      },
      // v5: rozkład proxy-score dla MCP forensics — pozwala zweryfikować czy top-N
      // rozprasza się rozsądnie czy wszystkie selected = score=1 (= bonus SOURCE_STEM
      // nie odróżnia wystarczająco i trzeba przeprojektować formułę score).
      claims_score_distribution: {
        score_5: claims.filter((c) => c.score === 5).length,
        score_4: claims.filter((c) => c.score === 4).length,
        score_3: claims.filter((c) => c.score === 3).length,
        score_2: claims.filter((c) => c.score === 2).length,
        score_1: claims.filter((c) => c.score === 1).length,
      },
      injection_stage: "pre_humanization",
      confirmed: 0, modified: 0, unverified: 0,
      verified_claims_count: 0,
      sources_inserted: 0,
      orphaned_inline_count: 0,
      footer_appended: false,
      cost_usd: 0,
    };

    if (verifiableClaims.length > 0) {
      const { verified, costUsd: enrichCost, urlQualityFiltered, filterReasons } =
        await verifyClaims(verifiableClaims, language, mapId, nodeId);
      cost.enrichment = enrichCost;
      const inj = insertSources(weavedHtml, verified, language);
      enrichedHtml = inj.html;
      enrichDebug.confirmed = verified.filter((v) => v.status === "confirmed").length;
      enrichDebug.modified = verified.filter((v) => v.status === "modified").length;
      enrichDebug.unverified = verified.filter((v) => v.status === "unverified").length;
      enrichDebug.verified_claims_count = verified.length;
      enrichDebug.sources_inserted = inj.injectedCount;
      enrichDebug.orphaned_inline_count = inj.orphanedCount;
      enrichDebug.footer_appended = inj.footerAppended;
      enrichDebug.citation_dedup = {
        total_claims: inj.totalClaimCount,
        unique_urls: inj.uniqueUrlCount,
        duplicates_collapsed: inj.totalClaimCount - inj.uniqueUrlCount,
        total_sup_tags_inline: inj.supTagCount,
        adjacent_sup_collapsed: inj.adjacentSupCollapsed,
      };
      enrichDebug.url_quality_filtered = {
        count: urlQualityFiltered,
        reasons: filterReasons,
      };
      const total = inj.injectedCount + inj.orphanedCount;
      if (total > 0 && inj.orphanedCount / total > 0.2) {
        enrichDebug.orphan_warning = true;
        enrichDebug.orphan_rate = +(inj.orphanedCount / total).toFixed(2);
      }
      enrichDebug.cost_usd = enrichCost;
      onProgress?.({
        stage: "enrichment",
        stageLabel: `Enrichment ✓ ${enrichDebug.confirmed}/${verifiableClaims.length} potwierdzone, ${inj.injectedCount} inline + footer — $${enrichCost.toFixed(3)}`,
        stageDebug: enrichDebug,
      });
    } else {
      onProgress?.({ stage: "enrichment", stageLabel: `Enrichment ✓ brak claimów do weryfikacji`, stageDebug: enrichDebug });
    }
    engineDebug.enrichment = enrichDebug;

    await (supabase as any)
      .from("thematic_node_content")
      .update({ enriched_html: enrichedHtml })
      .eq("node_id", nodeId);

    // ---- STAGE 7: Optimization ----
    onProgress?.({ stage: "optimization", stageLabel: `Optimization — 2-pass (Article Check + Flow)...` });
    await updateEngineStage(nodeId, "optimization");

    const optResult = await optimizeArticle(enrichedHtml, language, 1500, ctx.writingProfile, mapId, nodeId);
    cost.optimization = optResult.costUsd;

    const optDebug = {
      words_before: optResult.report.inputWords,
      words_after: optResult.report.outputWords,
      growth_percent: optResult.report.growthPercent,
      sources_preserved: optResult.report.sourcesPreserved,
      warnings: optResult.warnings,
      cost_usd: optResult.costUsd,
    };
    engineDebug.optimization = optDebug;
    onProgress?.({
      stage: "optimization",
      stageLabel: `Optimization ✓ ${optDebug.words_before}→${optDebug.words_after} słów (${optDebug.growth_percent > 0 ? "+" : ""}${optDebug.growth_percent}%), źródła: ${optDebug.sources_preserved ? "OK" : "UWAGA"} — $${optResult.costUsd.toFixed(3)}`,
      stageDebug: optDebug,
    });

    await (supabase as any)
      .from("thematic_node_content")
      .update({ optimized_html: optResult.optimizedHtml })
      .eq("node_id", nodeId);

    // ---- STAGE 8: Humanization ----
    onProgress?.({ stage: "humanization", stageLabel: `Humanization — 4 tiery + Writing Profile...` });
    await updateEngineStage(nodeId, "humanization");

    const humanResult = await humanizeArticle(optResult.optimizedHtml, mapId, nodeId, language);
    cost.humanization = humanResult.costUsd;

    const humanDebug = {
      ai_phrases_removed: humanResult.report.aiPhrasesRemoved,
      banned_phrases_found: humanResult.report.bannedPhrasesFound,
      cost_usd: humanResult.costUsd,
      // widocznosc strat linkow w humanizacji (2026-08-24: 3->1 linki bez sladu w debug/QA)
      warnings: humanResult.warnings,
    };
    engineDebug.humanization = humanDebug;
    onProgress?.({
      stage: "humanization",
      stageLabel: `Humanization ✓ ${humanDebug.ai_phrases_removed} AI fraz usunięto, ${humanDebug.banned_phrases_found} banned — $${humanResult.costUsd.toFixed(3)}`,
      stageDebug: humanDebug,
    });

    // (in-content images generujemy w PUBLISH flow → publishQueueService,
    //  zeby stare juz-gotowe artykuly tez dostawaly obrazki przy klikniciu Publikuj)

    // ---- STRAZNIK ZRODEL: modele potrafia DOPISAC zmyslona stopke Zrodla
    // (incydent 2026-08-23: oba URL-e 404), gdy enrichment nic nie wstawil ----
    const sourceGuard = stripFabricatedSources(humanResult.finalHtml, enrichedHtml);
    if (sourceGuard.changed) {
      logPublic("SOURCES", `Strażnik źródeł: usunięto ${sourceGuard.removedUrls.length} zmyślonych wpisów${sourceGuard.strippedFooter ? " (cała stopka)" : ""}: ${sourceGuard.removedUrls.join(", ")}`);
      engineDebug.sources_guard = { removed_urls: sourceGuard.removedUrls, stripped_footer: sourceGuard.strippedFooter };
    }
    const cleanFinalHtml = sourceGuard.html;

    // ---- QA GATE ----
    const auditResult = postContentAudit(cleanFinalHtml, ctx, engineDebug);
    engineDebug.qa = auditResult;

    onProgress?.({
      stage: "humanization",
      stageLabel: `QA ✓ score: ${auditResult.quality_score}/100, status: ${auditResult.qa_status}, sources: ${auditResult.has_sources ? "OK" : "BRAK"}, coverage: ${(auditResult.required_entities_coverage * 100).toFixed(0)}%`,
      stageDebug: auditResult,
    });

    // ---- DONE ----
    cost.total = cost.serp_research + cost.kg_assembly + cost.micro_eav + cost.fan_out + cost.outline + cost.draft + cost.link_weaving + cost.enrichment + cost.optimization + cost.humanization + cost.in_content_images;

    engineDebug.total_cost_usd = cost.total;
    engineDebug.total_duration_ms = Date.now() - startTime;

    const finalStage = auditResult.qa_status === "failed" ? "failed" : "ready";

    await updateEngineStage(nodeId, finalStage, {
      // udany run MUSI wyczyscic blad z poprzedniego nieudanego (8 stale rows 2026-08-23)
      engine_error: finalStage === "ready" ? null : undefined,
      // humanizationService zapisal final_html PRZED straznikiem zrodel —
      // nadpisz oczyszczonym tylko, gdy straznik cos wycial
      final_html: sourceGuard.changed ? cleanFinalHtml : undefined,
      engine_completed_at: new Date().toISOString(),
      engine_cost: cost,
      engine_debug: engineDebug,
      content_status: "content_ready",
      generated_from_context_hash: contextHash,
      current_context_hash: contextHash,
      is_stale: false,
      needs_regeneration: false,
      qa_status: auditResult.qa_status,
      quality_score: auditResult.quality_score,
      qa_checked_at: new Date().toISOString(),
    });

    endLogRun({
      success: true,
      stage: "ready",
      cost_total: cost.total,
      duration_s: Math.round((Date.now() - startTime) / 1000),
      quality_score: auditResult.quality_score,
    });

    return {
      success: true,
      stage: "ready",
      draftHtml: cleanFinalHtml,
      cost,
      durationMs: Date.now() - startTime,
    };
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : "Nieznany błąd";

    engineDebug.total_cost_usd = cost.total;
    engineDebug.total_duration_ms = Date.now() - startTime;
    engineDebug.error = errorMessage;

    await updateEngineStage(nodeId, "failed", {
      engine_error: errorMessage,
      engine_completed_at: new Date().toISOString(),
      engine_cost: cost,
      engine_debug: engineDebug,
    });

    cost.total = cost.serp_research + cost.kg_assembly + cost.micro_eav + cost.fan_out + cost.outline + cost.draft + cost.link_weaving + cost.enrichment + cost.optimization + cost.humanization + cost.in_content_images;

    endLogRun({
      success: false,
      stage: "failed",
      error: errorMessage,
      cost_total: cost.total,
      duration_s: Math.round((Date.now() - startTime) / 1000),
    });

    return {
      success: false,
      stage: "failed",
      error: errorMessage,
      cost,
      durationMs: Date.now() - startTime,
    };
  }
}

// =============================================
// SPLIT: generateStructure (Etap 1) + generateContent (Etap 2)
// =============================================

export interface StructureResult {
  success: boolean;
  outline?: Array<{ type: string; header: string | null; h3s?: Array<{ header: string }> }>;
  error?: string;
  cost: Partial<CostBreakdown>;
  durationMs: number;
}

/**
 * Etap 1: SERP → KG → Fan-Out → Outline → outline_ready
 * Generuje strukturę artykułu (nagłówki H2/H3) BEZ pisania treści.
 */
export async function generateStructure(
  nodeId: string,
  mapId: string,
  onProgress?: (progress: EngineProgress) => void,
): Promise<StructureResult> {
  // Existing Posts Awareness: skip check
  const skipDec = await shouldSkipNodeGeneration(nodeId);
  if (skipDec.skip && skipDec.reason) {
    await updateNodeSkipStatus(nodeId, skipDec.reason);
    return { success: true, cost: {}, durationMs: 0 };
  }

  const startTime = Date.now();
  const engineDebug: Record<string, unknown> = {};
  const cost: Partial<CostBreakdown> = {};

  startLogRun(`structure-${nodeId}`, { nodeId, mapId, type: "etap_1_structure" });

  try {
    onProgress?.({ stage: "serp_research", stageLabel: "Budowanie kontekstu node'a..." });

    const ctx = await buildNodeContentContext(nodeId, mapId);
    const contextHash = buildContextHash(ctx);
    engineDebug.input_context = { ...createContextSnapshot(ctx), context_hash: contextHash };

    const query = ctx.node.canonical_question;
    const seoTitle = ctx.node.title;
    const language = ctx.map.language;

    await (supabase as any).from("thematic_node_content").upsert(
      {
        node_id: nodeId, map_id: mapId,
        user_id: (await supabase.auth.getSession()).data.session?.user?.id,
        engine_stage: "serp_research",
        engine_started_at: new Date().toISOString(),
        content_status: "empty",
      },
      { onConflict: "node_id" },
    );

    // ---- SERP ----
    onProgress?.({ stage: "serp_research", stageLabel: `SERP Research — query: "${query}"` });
    await updateEngineStage(nodeId, "serp_research");
    const serpResult = await researchSERP(nodeId, query, mapId);
    cost.serp_research = 0.015;
    engineDebug.serp = serpResult.debug;

    // Per-URL log (works for both cache hit and fresh crawl)
    if (serpResult.cacheHit) {
      onProgress?.({ stage: "serp_research", stageLabel: `  📦 Cache hit — ${serpResult.debug.blocks_passed} bloków z cache (30 dni TTL)` });
    }
    for (const urlInfo of (serpResult.debug as any).per_url || []) {
      const domain = urlInfo.url.replace(/https?:\/\//, "").split("/")[0];
      onProgress?.({
        stage: "serp_research",
        stageLabel: urlInfo.crawled
          ? `  ✓ ${domain} — ${urlInfo.chars_total} znaków → ${urlInfo.chars_kept} kept`
          : `  ✗ ${domain} — ${urlInfo.error || "error"}`,
      });
    }
    const totalCharsAfter = (serpResult.debug as any).total_chars_after ?? serpResult.cleanContent.reduce((s: number, b: any) => s + (b.text?.length || 0), 0);
    onProgress?.({
      stage: "serp_research",
      stageLabel: `SERP ✓ ${serpResult.debug.urls_crawled}/${serpResult.debug.urls_fetched} URLs, ${serpResult.debug.blocks_passed} bloków [${serpResult.debug.filter_method}], ${totalCharsAfter} znaków łącznie`,
      stageDebug: serpResult.debug,
    });

    // ---- KG ----
    onProgress?.({ stage: "kg_assembly", stageLabel: `KG Assembly — "${ctx.node.primary_entity}", ${serpResult.cleanContent.length} źródeł → LLM...` });
    await updateEngineStage(nodeId, "kg_assembly");
    const { kg, costUsd: kgCost, debug: kgDebug } = await assembleKnowledgeGraph(
      nodeId, mapId, serpResult.cleanContent, query, ctx.node.primary_entity, language, ctx.knowledge,
      (subLabel) => onProgress?.({ stage: "kg_assembly", stageLabel: `  ${subLabel}` }),
      { document_role: ctx.node.document_role, boundaries_not_owned: ctx.map.boundaries_not_owned },
    );
    cost.kg_assembly = kgCost;
    engineDebug.kg = kgDebug;
    onProgress?.({
      stage: "kg_assembly",
      stageLabel: `KG ✓ ${kgDebug.entities} encji (${Object.entries(kgDebug.entity_types as Record<string, number>).map(([k, v]) => `${k}:${v}`).join(", ")}), ${kgDebug.facts} faktów, ${kgDebug.relationships} relacji — $${kgCost.toFixed(3)}`,
      stageDebug: kgDebug,
    });

    // ---- Micro-EAV for KG entities ----
    const microEavResult = await generateMicroEav(
      nodeId, mapId,
      kg.entities.map((e: any) => ({
        entity_name: e.entity_name,
        description: e.description,
        entity_type: e.type,
      })),
      language,
      { map_name: ctx.map.name, macro_statement: ctx.map.macro_statement, scope_in: ctx.map.scope_in || [], scope_out: ctx.map.scope_out || [] },
      seoTitle, ctx.node.document_role, ctx.node.canonical_question,
    );
    if (microEavResult.generatedCount > 0) {
      // GUARD 2026-08-24 — identyczna zasada jak w generateArticle (patrz
      // komentarz tam + eavGuard.ts): male .in() tylko na encje Micro-EAV,
      // merge zamiast nadpisania, blad/pusta lista nie zubaza kontekstu.
      // entityNameToDbId zawiera WSZYSTKIE encje mapy (Whiteever: 968) —
      // zawezamy do encji KG TEGO runu (~20-30), inaczej .in() znow pęka
      // (guard to zlapal jako "Bad Request" w pierwszym runie po fixie)
      const kgEntityNames = new Set(
        kg.entities.map((e: any) => String(e.entity_name || "").toLowerCase()).filter(Boolean),
      );
      const microEntries = Object.entries(microEavResult.entityNameToDbId || {}).filter(
        ([name]) => kgEntityNames.has(name.toLowerCase()),
      );
      const microIds = microEntries.map(([, id]) => id);
      const microIdToName = new Map(microEntries.map(([name, id]) => [id, name]));
      let freshRows: any[] | null = null;
      let freshError: unknown = null;
      if (microIds.length > 0) {
        const { data, error } = await (supabase as any)
          .from("thematic_entity_attributes")
          .select("entity_id, attribute_name, value, confidence")
          .in("entity_id", microIds)
          .gte("confidence", 0.7)
          .limit(50);
        freshRows = data;
        freshError = error;
      }
      const refresh = mergeFreshEav(
        ctx.knowledge.eav,
        (freshRows || []).map((a: any) => ({
          entity_name: microIdToName.get(a.entity_id) || a.entity_id,
          attribute_name: a.attribute_name,
          value: a.value,
          confidence: typeof a.confidence === "number" ? a.confidence : 0.7,
        })),
        { error: freshError, source: "Micro-EAV refresh" },
      );
      if (refresh.warning) logPublic("EAV", refresh.warning);
      else if (refresh.merged > 0) logDebug("EAV", `Micro-EAV merge: +${refresh.merged} atrybutów (razem ${refresh.eav.length})`);
      ctx.knowledge.eav = refresh.eav;
      onProgress?.({ stage: "kg_assembly", stageLabel: `Micro-EAV ✓ ${microEavResult.generatedCount} atrybutów — $${microEavResult.costUsd.toFixed(4)}` });
    }
    cost.micro_eav = microEavResult.costUsd;
    engineDebug.micro_eav = microEavResult.debug;

    // ---- Fan-Out ----
    onProgress?.({ stage: "fan_out", stageLabel: `Fan-Out — rozkład query...` });
    await updateEngineStage(nodeId, "fan_out");
    const { fanOut, costUsd: fanOutCost, debug: fanOutDebug } = await generateFanOut(
      nodeId, query, mapId, ctx.node.document_role, ctx.node.primary_entity, language, ctx.knowledge.eav,
      {
        seed_intent_family: ctx.seed.intent_family,
        macro_statement: ctx.map.macro_statement,
        boundaries_not_owned: ctx.map.boundaries_not_owned,
        market_keywords_top5: ctx.serp?.market_keywords_top5,
      },
      kg.ideations,
    );
    cost.fan_out = fanOutCost;
    engineDebug.fan_out = fanOutDebug;

    // Log micro-areas
    for (const area of (fanOut.micro_areas || []).slice(0, 10)) {
      onProgress?.({ stage: "fan_out", stageLabel: `  ${area.order}. [${area.intent}] ${area.area} — "${area.question}"` });
    }
    onProgress?.({
      stage: "fan_out",
      stageLabel: `Fan-Out ✓ ${fanOutDebug.micro_areas} mikro-obszarów, intent: ${fanOutDebug.primary_intent}, PAA: ${fanOutDebug.paa_merged} — $${fanOutCost.toFixed(3)}`,
      stageDebug: fanOutDebug,
    });

    // ---- Area Consolidation (optional) ----
    const consolidationResult = await consolidateAreas(fanOut, query, ctx.node.document_role, ctx.node.primary_entity, mapId, nodeId);
    if (consolidationResult.consolidated) {
      cost.fan_out += consolidationResult.costUsd;
      onProgress?.({
        stage: "fan_out",
        stageLabel: `Consolidation ✓ ${consolidationResult.originalCount} → ${consolidationResult.newCount} areas (${consolidationResult.mergesMade} merges)`,
      });
    }
    engineDebug.area_consolidation = consolidationResult;

    // ---- Outline ----
    onProgress?.({ stage: "outline", stageLabel: `Outline — budowanie H2/H3...` });
    await updateEngineStage(nodeId, "outline");

    // CLEANUP v1 #1 (2026-06-01): legacy isPillar branch removed dla generateStructure.
    // Spoki ZAWSZE szły gałęzią `: undefined` (isPillar=false), więc to no-op dla spoke flow.
    const { outline, costUsd: outlineCost, debug: outlineDebug } = await generateOutline(
      nodeId, mapId, fanOut, kg, seoTitle, language,
      {
        document_role: ctx.node.document_role,
        primary_entity: ctx.node.primary_entity,
        canonical_question: ctx.node.canonical_question,
        validated_question: ctx.serp?.validated_question,
        best_market_keyword: ctx.serp?.best_market_keyword,
        market_keywords_top5: ctx.serp?.market_keywords_top5,
        boundaries_not_owned: ctx.map.boundaries_not_owned,
      },
      ctx.knowledge.eav,
      microEavResult.entityNameToDbId,
      undefined, // outlineOptions: pillar legacy removed (CLEANUP v1 #1)
    );
    cost.outline = outlineCost;
    engineDebug.outline = outlineDebug;

    // Log outline tree
    for (const section of outline.sections) {
      if (section.type === "intro") {
        onProgress?.({ stage: "outline", stageLabel: `  â”śâ”€ Intro` });
      } else {
        const h3Count = (section.h3s || []).length;
        const headerText = section.header || section.source_area || `sekcja #${section.order}`;
        onProgress?.({ stage: "outline", stageLabel: `  â”śâ”€ H2: ${headerText} (${section.section_type}, ${h3Count} H3)` });
        for (const h3 of (section.h3s || [])) {
          onProgress?.({ stage: "outline", stageLabel: `  â”‚  â””â”€ H3: ${h3.header}` });
        }
      }
    }
    onProgress?.({
      stage: "outline",
      stageLabel: `Outline ✓ ${outlineDebug.h2_sections} H2, ${outlineDebug.h3_total} H3, ${outlineDebug.kg_items_distributed} KG items — $${outlineCost.toFixed(3)}`,
      stageDebug: {
        ...outlineDebug,
        sections: outline.sections.map((s) => ({
          type: s.type, order: s.order, header: s.header, section_type: s.section_type,
          h3s: (s.h3s || []).map((h) => h.header),
          source_area: s.source_area, source_intent: s.source_intent,
        })),
      },
    });

    // ---- Save as outline_ready ----
    // Save actual outline + KG data for Etap 2
    await updateEngineStage(nodeId, "outline_ready", {
      outline: outline,
      fan_out: fanOut,
      engine_debug: engineDebug,
      engine_cost: cost,
      current_context_hash: contextHash,
    });

    endLogRun({
      success: true,
      h2: outlineDebug.h2_sections,
      h3: outlineDebug.h3_total,
      duration_s: Math.round((Date.now() - startTime) / 1000),
    });

    return {
      success: true,
      outline: outline.sections.map((s) => ({
        type: s.type,
        header: s.header,
        h3s: (s.h3s || []).map((h) => ({ header: h.header })),
      })),
      cost,
      durationMs: Date.now() - startTime,
    };
  } catch (err) {
    const msg = err instanceof Error ? err.message : "Nieznany błąd";
    await updateEngineStage(nodeId, "failed", { engine_error: msg, engine_debug: engineDebug });
    endLogRun({ success: false, error: msg, duration_s: Math.round((Date.now() - startTime) / 1000) });
    return { success: false, error: msg, cost, durationMs: Date.now() - startTime };
  }
}

/**
 * Etap 2: Draft → Enrichment → Optimization → Humanization → QA
 * Wymaga: engine_stage === "outline_ready"
 * Ĺaduje outline + KG z DB, nie powtarza etapów 1-4.
 */
export async function generateContent(
  nodeId: string,
  mapId: string,
  onProgress?: (progress: EngineProgress) => void,
): Promise<ArticleResult> {
  // Existing Posts Awareness: skip check
  const skipDec = await shouldSkipNodeGeneration(nodeId);
  if (skipDec.skip && skipDec.reason) {
    await updateNodeSkipStatus(nodeId, skipDec.reason);
    return {
      success: true,
      stage: "skipped_existing" as EngineStage,
      cost: {
        serp_research: 0, kg_assembly: 0, micro_eav: 0, fan_out: 0, outline: 0,
        draft: 0, link_weaving: 0, enrichment: 0, optimization: 0, humanization: 0, in_content_images: 0, total: 0,
      },
      durationMs: 0,
    };
  }

  const startTime = Date.now();
  const engineDebug: Record<string, unknown> = {};
  const cost: CostBreakdown = {
    serp_research: 0, kg_assembly: 0, fan_out: 0, outline: 0,
    draft: 0, enrichment: 0, optimization: 0, humanization: 0, in_content_images: 0, total: 0,
  };

  startLogRun(`content-${nodeId}`, { nodeId, mapId, type: "etap_2_content" });

  try {
    // Ensure writing profile exists before content generation
    const autoProfile = await ensureActiveWritingProfile(mapId);
    logStage("CONTENT", "START", { nodeId, mapId, profile: autoProfile ? "active" : "none" });

    // Check: outline must be ready + load saved data
    const { data: existingContent } = await (supabase as any)
      .from("thematic_node_content")
      .select("engine_stage, outline, fan_out, engine_debug, engine_cost, current_context_hash")
      .eq("node_id", nodeId)
      .single();

    if (!existingContent || existingContent.engine_stage !== "outline_ready") {
      throw new Error(`Node nie ma gotowej struktury (stage: ${existingContent?.engine_stage || "brak"}). Najpierw uruchom "Przygotuj strukturę".`);
    }

    const outline = existingContent.outline;
    if (!outline?.sections || outline.sections.length === 0) {
      throw new Error("Brak zapisanego outline w DB. Uruchom ponownie Etap 1.");
    }

    // Restore previous engine_debug from Etap 1
    Object.assign(engineDebug, existingContent.engine_debug || {});
    // Restore costs from Etap 1 (ZAWIERA micro_eav — brakujacy wczesniej
    // powodowal ze engine_cost po Etap 2 gubilo ten klucz z Etap 1).
    const prevCost = existingContent.engine_cost || {};
    cost.serp_research = prevCost.serp_research || 0;
    cost.kg_assembly = prevCost.kg_assembly || 0;
    cost.micro_eav = prevCost.micro_eav || 0;
    cost.fan_out = prevCost.fan_out || 0;
    cost.outline = prevCost.outline || 0;

    // Load KG from content_knowledge_graphs
    const { data: kgRow } = await (supabase as any)
      .from("content_knowledge_graphs")
      .select("kg_data")
      .eq("node_id", nodeId)
      .single();

    const kg = kgRow?.kg_data || { entities: [], facts: [], relationships: [], ideations: [] };

    // Build context for writing profile + internal links
    const ctx = await buildNodeContentContext(nodeId, mapId);
    const contextHash = existingContent.current_context_hash || buildContextHash(ctx);
    const seoTitle = ctx.node.title;
    const language = ctx.map.language;

    onProgress?.({ stage: "draft", stageLabel: `Etap 2 — ładowanie outline (${outline.sections.length} sekcji) + KG...` });

    // ---- STAGE 5: Block-by-Block Draft ----
    const totalBlocks = outline.sections.filter((s: any) => s.type === "h2").length;
    onProgress?.({ stage: "draft", stageLabel: `Draft — ${totalBlocks} bloków do napisania...` });
    await updateEngineStage(nodeId, "draft");

    // CLEANUP v1 #1 (2026-06-01): legacy isPillar branch removed dla generateContent.
    // Spoki ZAWSZE szły gałęzią `: undefined` (isPillar=false), więc to no-op dla spoke flow.
    const { draftHtml, costUsd: draftCost, debug: draftDebug } = await generateDraft(
      nodeId, mapId, outline, kg, seoTitle, language,
      (current, total) => {
        onProgress?.({
          stage: "draft",
          stageLabel: `Draft — blok ${current}/${total}...`,
          blockProgress: { current, total },
        });
      },
      ctx.writingProfile,
      ctx.knowledge.internal_link_suggestions,
      ctx.node.document_role,
      ctx.knowledge.eav,
      {
        primary_entity: ctx.node.primary_entity,
        canonical_question: ctx.node.canonical_question,
      },
      undefined, // draftOptions: pillar legacy removed (CLEANUP v1 #1)
    );
    cost.draft = draftCost;
    engineDebug.draft = draftDebug;
    onProgress?.({
      stage: "draft",
      stageLabel: `Draft ✓ ${draftDebug.total_words} słów, ${draftDebug.blocks_generated} bloków — $${draftCost.toFixed(3)}`,
      stageDebug: draftDebug,
    });


    // ---- STAGE 5.5: Link Weaving ("najpierw pisz, potem linkuj") ----
    // Jeden call na caly artykul; kod aplikuje plan deterministycznie (max 1x
    // per target). Standalone pomijany — artykuly bez linkow wewnetrznych.
    let weavedHtml = draftHtml;
    if (!isStandaloneMode()) {
      onProgress?.({ stage: "draft", stageLabel: "Link Weaver — wplatanie linkow wewnetrznych..." });
      const weave = await weaveLinks(draftHtml, ctx.knowledge.internal_link_suggestions.slice(0, 5), {
        language,
        selfSlug: ctx.node.slug,
        nodeId,
      });
      weavedHtml = weave.html;
      cost.link_weaving = weave.costUsd;
      engineDebug.link_weaving = weave.debug;
      const appliedN = Array.isArray((weave.debug as Record<string, unknown>).applied)
        ? ((weave.debug as Record<string, unknown>).applied as unknown[]).length
        : 0;
      onProgress?.({ stage: "draft", stageLabel: `Link Weaver ✓ ${appliedN} linkow wplecionych — $${weave.costUsd.toFixed(4)}` });
    }

    // ---- STAGE 6: Data Enrichment ----
    const wordCount = weavedHtml.split(/\s+/).length;
    onProgress?.({ stage: "enrichment", stageLabel: `Enrichment — ekstrakcja claimów z ${wordCount} słów...` });
    await updateEngineStage(nodeId, "enrichment");

    let claims = await extractClaims(weavedHtml, language);
    if (claims.length === 0) {
      // FALLBACK (2026-08-24): slowniki/definicje bez liczb daja 0 twierdzen
      // -> weryfikacja nie startowala -> artykul bez zrodel. Weryfikujemy
      // pierwsze zdanie wstepu (glowna teze) przez web_search.
      const thesis = buildThesisFallbackClaim(weavedHtml, ctx.node.title);
      if (thesis) {
        claims = [thesis];
        logPublic("ENRICHMENT", "0 twierdzeń z ekstraktora — fallback: weryfikacja głównej tezy artykułu przez web_search");
      }
    }
    const verifiableClaims = getVerifiableClaims(claims);
    let enrichedHtml = weavedHtml;

    const enrichDebug: Record<string, unknown> = {
      claims_found: claims.length, claims_verified: verifiableClaims.length,
      language_used: language,
      claims_by_trigger: {
        date: claims.filter((c) => c.triggers.includes("DATE")).length,
        numeric_fact: claims.filter((c) => c.triggers.includes("NUMERIC_FACT")).length,
        named_reference: claims.filter((c) => c.triggers.includes("NAMED_REFERENCE")).length,
        source_stem: claims.filter((c) => c.triggers.includes("SOURCE_STEM")).length,
      },
      // v5: rozkład proxy-score dla MCP forensics — pozwala zweryfikować czy top-N
      // rozprasza się rozsądnie czy wszystkie selected = score=1 (= bonus SOURCE_STEM
      // nie odróżnia wystarczająco i trzeba przeprojektować formułę score).
      claims_score_distribution: {
        score_5: claims.filter((c) => c.score === 5).length,
        score_4: claims.filter((c) => c.score === 4).length,
        score_3: claims.filter((c) => c.score === 3).length,
        score_2: claims.filter((c) => c.score === 2).length,
        score_1: claims.filter((c) => c.score === 1).length,
      },
      injection_stage: "pre_humanization",
      confirmed: 0, modified: 0, unverified: 0,
      verified_claims_count: 0,
      sources_inserted: 0,
      orphaned_inline_count: 0,
      footer_appended: false,
      cost_usd: 0,
    };

    if (verifiableClaims.length > 0) {
      const { verified, costUsd: enrichCost, urlQualityFiltered, filterReasons } =
        await verifyClaims(verifiableClaims, language, mapId, nodeId);
      cost.enrichment = enrichCost;
      const inj = insertSources(weavedHtml, verified, language);
      enrichedHtml = inj.html;
      enrichDebug.confirmed = verified.filter((v) => v.status === "confirmed").length;
      enrichDebug.modified = verified.filter((v) => v.status === "modified").length;
      enrichDebug.unverified = verified.filter((v) => v.status === "unverified").length;
      enrichDebug.verified_claims_count = verified.length;
      enrichDebug.sources_inserted = inj.injectedCount;
      enrichDebug.orphaned_inline_count = inj.orphanedCount;
      enrichDebug.footer_appended = inj.footerAppended;
      enrichDebug.citation_dedup = {
        total_claims: inj.totalClaimCount,
        unique_urls: inj.uniqueUrlCount,
        duplicates_collapsed: inj.totalClaimCount - inj.uniqueUrlCount,
        total_sup_tags_inline: inj.supTagCount,
        adjacent_sup_collapsed: inj.adjacentSupCollapsed,
      };
      enrichDebug.url_quality_filtered = {
        count: urlQualityFiltered,
        reasons: filterReasons,
      };
      const total = inj.injectedCount + inj.orphanedCount;
      if (total > 0 && inj.orphanedCount / total > 0.2) {
        enrichDebug.orphan_warning = true;
        enrichDebug.orphan_rate = +(inj.orphanedCount / total).toFixed(2);
      }
      enrichDebug.cost_usd = enrichCost;
      onProgress?.({
        stage: "enrichment",
        stageLabel: `Enrichment ✓ ${enrichDebug.confirmed}/${verifiableClaims.length} potwierdzone, ${inj.injectedCount} inline + footer — $${enrichCost.toFixed(3)}`,
        stageDebug: enrichDebug,
      });
    } else {
      onProgress?.({ stage: "enrichment", stageLabel: `Enrichment ✓ brak claimów do weryfikacji`, stageDebug: enrichDebug });
    }
    engineDebug.enrichment = enrichDebug;

    await (supabase as any).from("thematic_node_content").update({ enriched_html: enrichedHtml }).eq("node_id", nodeId);

    // ---- STAGE 7: Optimization ----
    onProgress?.({ stage: "optimization", stageLabel: `Optimization — 2-pass (Article Check + Flow)...` });
    await updateEngineStage(nodeId, "optimization");

    const optResult = await optimizeArticle(enrichedHtml, language, 1500, ctx.writingProfile, mapId, nodeId);
    cost.optimization = optResult.costUsd;
    const optDebug = {
      words_before: optResult.report.inputWords, words_after: optResult.report.outputWords,
      growth_percent: optResult.report.growthPercent, sources_preserved: optResult.report.sourcesPreserved,
      warnings: optResult.warnings, cost_usd: optResult.costUsd,
    };
    engineDebug.optimization = optDebug;
    onProgress?.({
      stage: "optimization",
      stageLabel: `Optimization ✓ ${optDebug.words_before}→${optDebug.words_after} słów (${optDebug.growth_percent > 0 ? "+" : ""}${optDebug.growth_percent}%), źródła: ${optDebug.sources_preserved ? "OK" : "UWAGA"} — $${optResult.costUsd.toFixed(3)}`,
      stageDebug: optDebug,
    });

    await (supabase as any).from("thematic_node_content").update({ optimized_html: optResult.optimizedHtml }).eq("node_id", nodeId);

    // ---- STAGE 8: Humanization ----
    onProgress?.({ stage: "humanization", stageLabel: `Humanization — 4 tiery + Writing Profile...` });
    await updateEngineStage(nodeId, "humanization");

    const humanResult = await humanizeArticle(optResult.optimizedHtml, mapId, nodeId, language);
    cost.humanization = humanResult.costUsd;
    const humanDebug = {
      ai_phrases_removed: humanResult.report.aiPhrasesRemoved,
      banned_phrases_found: humanResult.report.bannedPhrasesFound,
      cost_usd: humanResult.costUsd,
      // widocznosc strat linkow w humanizacji (2026-08-24: 3->1 linki bez sladu w debug/QA)
      warnings: humanResult.warnings,
    };
    engineDebug.humanization = humanDebug;
    onProgress?.({
      stage: "humanization",
      stageLabel: `Humanization ✓ ${humanDebug.ai_phrases_removed} AI fraz usunięto, ${humanDebug.banned_phrases_found} banned — $${humanResult.costUsd.toFixed(3)}`,
      stageDebug: humanDebug,
    });

    // (in-content images generujemy w PUBLISH flow → publishQueueService,
    //  zeby stare juz-gotowe artykuly tez dostawaly obrazki przy klikniciu Publikuj)

    // ---- STRAZNIK ZRODEL: modele potrafia DOPISAC zmyslona stopke Zrodla
    // (incydent 2026-08-23: oba URL-e 404), gdy enrichment nic nie wstawil ----
    const sourceGuard = stripFabricatedSources(humanResult.finalHtml, enrichedHtml);
    if (sourceGuard.changed) {
      logPublic("SOURCES", `Strażnik źródeł: usunięto ${sourceGuard.removedUrls.length} zmyślonych wpisów${sourceGuard.strippedFooter ? " (cała stopka)" : ""}: ${sourceGuard.removedUrls.join(", ")}`);
      engineDebug.sources_guard = { removed_urls: sourceGuard.removedUrls, stripped_footer: sourceGuard.strippedFooter };
    }
    const cleanFinalHtml = sourceGuard.html;

    // ---- QA GATE ----
    const auditResult = postContentAudit(cleanFinalHtml, ctx, engineDebug);
    engineDebug.qa = auditResult;
    onProgress?.({
      stage: "humanization",
      stageLabel: `QA ✓ score: ${auditResult.quality_score}/100, status: ${auditResult.qa_status}, sources: ${auditResult.has_sources ? "OK" : "BRAK"}, coverage: ${(auditResult.required_entities_coverage * 100).toFixed(0)}%`,
      stageDebug: auditResult,
    });

    // ---- DONE ----
    cost.total = cost.serp_research + cost.kg_assembly + cost.micro_eav + cost.fan_out + cost.outline + cost.draft + cost.link_weaving + cost.enrichment + cost.optimization + cost.humanization + cost.in_content_images;
    engineDebug.total_cost_usd = cost.total;
    engineDebug.total_duration_ms = Date.now() - startTime;

    const finalStage = auditResult.qa_status === "failed" ? "failed" : "ready";

    await updateEngineStage(nodeId, finalStage, {
      // udany run MUSI wyczyscic blad z poprzedniego nieudanego (8 stale rows 2026-08-23)
      engine_error: finalStage === "ready" ? null : undefined,
      final_html: cleanFinalHtml,
      engine_completed_at: new Date().toISOString(),
      engine_cost: cost,
      engine_debug: engineDebug,
      content_status: "content_ready",
      generated_from_context_hash: contextHash,
      current_context_hash: contextHash,
      is_stale: false,
      needs_regeneration: false,
      qa_status: auditResult.qa_status,
      quality_score: auditResult.quality_score,
      qa_checked_at: new Date().toISOString(),
    });

    endLogRun({
      success: true,
      stage: "ready",
      cost_total: cost.total,
      duration_s: Math.round((Date.now() - startTime) / 1000),
    });

    return {
      success: true, stage: "ready", draftHtml: cleanFinalHtml,
      cost, durationMs: Date.now() - startTime,
    };
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : "Nieznany błąd";
    engineDebug.error = errorMessage;
    await updateEngineStage(nodeId, "failed", { engine_error: errorMessage, engine_debug: engineDebug });
    cost.total = cost.serp_research + cost.kg_assembly + cost.micro_eav + cost.fan_out + cost.outline + cost.draft + cost.link_weaving + cost.enrichment + cost.optimization + cost.humanization + cost.in_content_images;
    endLogRun({
      success: false,
      error: errorMessage,
      cost_total: cost.total,
      duration_s: Math.round((Date.now() - startTime) / 1000),
    });
    return { success: false, stage: "failed", error: errorMessage, cost, durationMs: Date.now() - startTime };
  }
}

// Re-export sub-services for direct use
export { researchSERP } from "./serpResearch";
export { assembleKnowledgeGraph } from "./knowledgeGraph";
export { generateFanOut } from "./queryFanOut";
export { generateOutline } from "./outlineGenerator";
export { generateDraft } from "./draftGenerator";
export { extractClaims, getVerifiableClaims } from "./claimExtractor";
export { verifyClaims, insertSources } from "./webSearchVerifier";
export { optimizeArticle } from "./optimizationService";
export { humanizeArticle } from "./humanizationService";
